import { useEffect, useId, useMemo, useRef, useState } from 'react'

type PreviewPageJumpProps = {
  page: number
  totalPages: number
  disabled?: boolean
  onChange: (pageIndex: number) => void
}

const parsePageNumber = (raw: string, totalPages: number): number | null => {
  const parsed = Number.parseInt(raw.trim(), 10)
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > totalPages) {
    return null
  }
  return parsed
}

export const PreviewPageJump = ({
  page,
  totalPages,
  disabled = false,
  onChange,
}: PreviewPageJumpProps) => {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(String(page + 1))

  useEffect(() => {
    if (!open) {
      setQuery(String(page + 1))
    }
  }, [open, page])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const commit = (raw: string) => {
    const parsed = parsePageNumber(raw, totalPages)
    if (parsed == null) {
      setQuery(String(page + 1))
      setOpen(false)
      return
    }
    onChange(parsed - 1)
    setQuery(String(parsed))
    setOpen(false)
  }

  const filteredPages = useMemo(() => {
    const pages = Array.from({ length: totalPages }, (_, index) => String(index + 1))
    const trimmed = query.trim()
    if (!trimmed || trimmed === String(page + 1)) {
      return pages
    }
    return pages.filter((label) => label.includes(trimmed))
  }, [page, query, totalPages])

  const showList = open && !disabled

  return (
    <div ref={rootRef} className="filter-combobox market-pagination__jump-combo">
      <input
        className="echat-field__input market-pagination__page-select"
        value={query}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-label="Номер страницы предпросмотра"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => commit(query)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit(query)
          }
          if (event.key === 'Escape') {
            setQuery(String(page + 1))
            setOpen(false)
          }
        }}
      />
      {showList ? (
        <ul id={listId} className="filter-combobox__list" role="listbox">
          {filteredPages.length === 0 ? (
            <li className="filter-combobox__hint">Нет такой страницы</li>
          ) : (
            filteredPages.map((label) => (
              <li key={label}>
                <button
                  type="button"
                  className="filter-combobox__option"
                  role="option"
                  aria-selected={label === String(page + 1)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commit(label)}
                >
                  {label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
